import { useCallback, useEffect, useRef, useState } from "react";

export type RfiSaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

export interface RfiSaveState {
  handleSave: () => Promise<boolean>;
  hasUnsavedChanges: boolean;
  status: RfiSaveStatus;
}

// Mount a separate instance for each project. The API replaces the entire list,
// so writes must be serialized and newly created IDs carried into the next write.
export function useRfiAutosave<T extends { id: string }>({
  enabled,
  save,
  onError,
}: {
  enabled: boolean;
  save: (items: T[]) => Promise<T[]>;
  onError: (error: unknown) => void;
}) {
  const [items, setItems] = useState<T[]>([]);
  const [status, setStatus] = useState<RfiSaveStatus>("idle");
  const options = useRef({ enabled, save, onError });
  options.current = { enabled, save, onError };
  const state = useRef({
    items: [] as T[],
    revision: 0,
    savedRevision: 0,
    backendIds: new Map<string, string>(),
    timer: undefined as ReturnType<typeof setTimeout> | undefined,
    inFlight: null as Promise<boolean> | null,
  });

  const flush = useCallback((): Promise<boolean> => {
    const current = state.current;
    clearTimeout(current.timer);
    if (current.inFlight) return current.inFlight;
    if (current.revision === current.savedRevision) return Promise.resolve(true);
    if (!options.current.enabled) return Promise.resolve(false);

    current.inFlight = (async () => {
      try {
        while (current.savedRevision !== current.revision) {
          if (!options.current.enabled) return false;
          const revision = current.revision;
          const snapshot = current.items;
          setStatus("saving");
          const saved = await options.current.save(snapshot.map(item => ({
            ...item,
            id: current.backendIds.get(item.id) ?? item.id,
          })));
          // The sync endpoint returns rows in payload order. Keep stable UI IDs
          // and never replace current text/order with an older server response.
          snapshot.forEach((item, index) => current.backendIds.set(item.id, saved[index].id));
          current.savedRevision = revision;
        }
        setStatus("saved");
        return true;
      } catch (error) {
        setStatus("error");
        options.current.onError(error);
        return false;
      }
    })().finally(() => {
      current.inFlight = null;
    });
    return current.inFlight;
  }, []);

  const resetItems = useCallback((loaded: T[]) => {
    state.current.items = loaded;
    state.current.backendIds.clear();
    state.current.revision = 0;
    state.current.savedRevision = 0;
    setItems(loaded);
    setStatus("idle");
  }, []);

  const editItems = useCallback((update: (previous: T[]) => T[]) => {
    if (!options.current.enabled) return;
    const current = state.current;
    const next = update(current.items);
    if (next.length === current.items.length && next.every((item, index) => item === current.items[index])) return;
    current.items = next;
    current.revision += 1;
    setItems(next);
    setStatus(current.inFlight ? "saving" : "pending");
    clearTimeout(current.timer);
    current.timer = setTimeout(() => { void flush(); }, 600);
  }, [flush]);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (state.current.revision === state.current.savedRevision) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const online = () => { void flush(); };
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("online", online);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("online", online);
      // SPA navigation must not cancel the debounce and silently drop an edit.
      void flush();
    };
  }, [flush]);

  return {
    items,
    editItems,
    resetItems,
    handleSave: flush,
    hasUnsavedChanges: state.current.revision !== state.current.savedRevision,
    status,
  };
}
